import { Component, computed, EventEmitter, Output } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { selectDisplayTitle, selectShipmentEditorRoute } from '../../../store/interface/interface.state';
import {
    selectOrganizationName,
    selectPackagingTypes,
    selectProfileBusy,
    selectProfileReady,
    selectProfileUsername,
    selectUserEmail
} from '../../../store/profile/profile.state';
import { profileReload, profileSelectIcons, profileSelectTheme } from '../../../store/profile/profile.actions';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { logout } from 'src/app/store/login/login.actions';
import {
    selectQuickQuote, selectShipmentEditable, selectShipmentEditorActionsBusy,
    selectShipmentEditorDisplayQuotes, selectShipmentEditorId, selectShipmentEditorLoading,
    selectShipmentEditorShipment, selectShipmentEditorValid
} from '../../../store/shipment-editor/shipment-editor.state';
import {
    shipmentEditorDisplayEditor,
    shipmentEditorOpenDocumentUploadModal,
    shipmentEditorDisplayQuotes,
    shipmentEditorGetQuotes,
    shipmentEditorLoadRequest,
    shipmentEditorSaveRequest, shipmentEditorSetMode, shipmentEditorSwapAddresses
} from '../../../store/shipment-editor/shipment-editor.actions';
import { quotingClose } from '../../../store/quote-progress/quote-progress.actions';
import { ShipmentService } from '../../../data/shipment.service';
import { ShipmentStatusEnum } from '../../../enum/shipment-status.enum';
import { ActivatedRoute, Route } from '@angular/router';
import { ProfileAvatarComponent } from '../profile-avatar/profile-avatar.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { ActionIndicatorComponent } from '../action-indicator/action-indicator.component';
import { ExportComponent } from '../export/export.component';
import { ShipmentUndoRedoComponent } from '../../shipment/shipment-undo-redo/shipment-undo-redo.component';
import { DocumentIndicatorComponent } from '../document-indicator/document-indicator.component';
import { MatTooltip } from '@angular/material/tooltip';
import { SaveIndicatorComponent } from '../../shipment/save-indicator/save-indicator.component';
import { IconComponent } from '../../shared/icon/icon.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { DocumentService } from '../../shared/services/document.service';
import { PickupBookingService } from '../../shared/services/pickup-booking.service';
import { PickupActionEnum } from '../../../enum/pickup-action.enum';
import { ConfirmService } from '../../shared/services/confirm.service';
import { shipmentVoidMultiRequest } from '../../../store/shipment/shipment.actions';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { ShipmentCategoryComponent } from '../../shipment/shipment-category/shipment-category.component';
import { PackagingTypeEnum } from '../../../enum/packaging-type.enum';
import { selectRouteDataParam } from '../../../store/router/router.state';
import { ResolverStatusService } from '../../shared/services/resolver-status.services';
import { ProfileService } from '../../shared/services/profile.service';

@Component({
    selector: 'app-top-toolbar',
    templateUrl: './top-toolbar.component.html',
    styleUrl: './top-toolbar.component.scss',
    imports: [MatToolbar, MatToolbarRow, MatIconButton, IconComponent, SaveIndicatorComponent, MatButton, MatTooltip,
        DocumentIndicatorComponent, ShipmentUndoRedoComponent, ExportComponent, ActionIndicatorComponent, MatMenuTrigger,
        ProfileAvatarComponent, MatMenu, MatMenuItem, MatSlideToggle, AsyncPipe, MatButtonToggleGroup, MatButtonToggle,
        ShipmentCategoryComponent]
})
export class TopToolbarComponent {

    @Output()
    toggleDrawer = new EventEmitter<void>();

    @Output()
    toggleSearch = new EventEmitter<void>();

    id: string;

    protected readonly print = print;

    constructor(private breakpointObserver: BreakpointObserver, private store: Store<AppState>,
                private shipmentService: ShipmentService,
                private documentService: DocumentService, private pickupBookingService: PickupBookingService,
                private confirmService: ConfirmService, private route: ActivatedRoute, public resolverStatus: ResolverStatusService,
                private profileService: ProfileService) {}

    get deepestRoute(): ActivatedRoute {
        let r = this.route;
        while (r.firstChild) { r = r.firstChild; }
        return r;
    }

    shipment$ = this.store.select(selectShipmentEditorShipment);

    shipmentToolbar$ = combineLatest([ this.shipment$, this.store.select(selectRouteDataParam('toolbar')) ]).pipe(
        map(([ shipment, toolbar ]) => !!shipment && toolbar === 'shipment')
    );

    currentView = this.store.selectSignal(selectRouteDataParam('view'));

    isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
        .pipe(
            map(result => result.matches),
            shareReplay()
        );

    title$: Observable<string> = this.store.select(selectDisplayTitle);
    profileBusy$: Observable<boolean> = this.store.select(selectProfileBusy);
    profileReady$: Observable<boolean> = this.store.select(selectProfileReady);
    username$: Observable<string> = this.store.select(selectProfileUsername);
    organizationName$: Observable<string> = this.store.select(selectOrganizationName);
    userEmail$: Observable<string> = this.store.select(selectUserEmail);
    displayQuoting = computed(() => this.currentView() === 'quoting');

    displayEditor$: Observable<boolean> = combineLatest([
        this.store.select(selectShipmentEditorId),
        this.store.select(selectShipmentEditorDisplayQuotes),
        this.store.select(selectShipmentEditable),
        this.store.select(selectShipmentEditorLoading)
    ]).pipe(
        map(([ id, displayQuoting, editable, loading ]) => !!id && editable && !displayQuoting && !loading)
    );
    displayUndoRedo$: Observable<boolean> = combineLatest([
        this.displayEditor$,
        this.isHandset$
    ]).pipe(
        map(([ displayEditor, handset ]) => displayEditor && !handset)
    );
    quickQuoteMode$: Observable<boolean> = this.store.select(selectQuickQuote);
    shipmentId$: Observable<string> = this.store.select(selectShipmentEditorId).pipe(
        tap((id) => this.id = id)
    );
    loading$: Observable<boolean> = this.store.select(selectShipmentEditorLoading);
    busy$: Observable<boolean> = combineLatest([ this.loading$, this.store.select(selectShipmentEditorActionsBusy) ]).pipe(
        map(([ loading, active ]) => loading || active)
    );
    editorValid$ = this.store.select(selectShipmentEditorValid);
    reloadAvailable$ = combineLatest([ this.loading$, this.editorValid$ ]).pipe(
        map(([ loading, valid ]) => !loading && !valid)
    );
    validQuotesAvailable = this.store.select(selectShipmentEditorId).pipe(
        filter(id => !!id),
        switchMap(id => this.shipmentService.observeValidQuoteCount(id)),
        map(count => count > 0)
    );

    canVoid$ = this.shipment$.pipe(
        map(shipment => shipment.statusCode === ShipmentStatusEnum.MANIFESTED)
    );
    canPrintWaybills$ = this.shipment$.pipe(
        map(shipment => shipment.documents.length > 0)
    );
    canBookPickup$ = this.shipment$.pipe(
        map(shipment => shipment.allowedPickupActions.indexOf(PickupActionEnum.BOOK_PICKUP) !== -1)
    );
    canCancelPickup$ = this.shipment$.pipe(
        map(shipment => shipment.allowedPickupActions.indexOf(PickupActionEnum.CANCEL_PICKUP) !== -1)
    );

    reloadProfile(): void {
        this.store.dispatch(profileReload());
    }

    logout(): void {
        this.store.dispatch(logout());
    }

    save(): void {
        this.store.dispatch(shipmentEditorSaveRequest());
    }

    endQuoting(): void {
        this.store.dispatch(quotingClose({ id: this.id }));
    }

    showQuotes(): void {
        this.store.dispatch(shipmentEditorDisplayQuotes());
    }

    showEditor(): void {

        this.store.dispatch(shipmentEditorDisplayEditor());
    }

    switchQuickQuote($event): void {
        this.store.dispatch(shipmentEditorSetMode({ quickQuote: $event.checked }));
    }

    bookPickup(): void {
        this.shipmentId$.pipe(take(1)).subscribe(id => this.pickupBookingService.openPickupBookingShipments([id]));
    }

    cancelPickup(): void {
        this.shipment$.pipe(take(1)).subscribe(shipment => this.pickupBookingService.openPickupCancel([shipment.pickup.id]));
    }

    startQuoting(): void {
        this.store.dispatch(shipmentEditorGetQuotes());
    }

    voidShipment(): void {
        this.shipmentId$.pipe(take(1)).subscribe(id => {
            this.confirmService.action(
                $localize`Void Shipment`,
                $localize`Are you sure you wish to void this shipment?`,
                $localize`Void`,
                () => this.store.dispatch(shipmentVoidMultiRequest({ shipmentIds: [id] })));
        });
    }

    printWaybills(): void {
        this.shipmentId$.pipe(take(1)).subscribe(id => this.documentService.openShipmentWaybills([id]));
    }

    swapEditorAddresses(): void {
        this.store.dispatch(shipmentEditorSwapAddresses());
    }

    openDocumentUpload(): void {
        this.store.dispatch(shipmentEditorOpenDocumentUploadModal({}));
    }

    openProfileDialog(): void {
        this.profileService.openProfileDialog();
    }
}
